import { useEffect, useRef } from "react";
import { Button } from "./Button";
import { WordReveal } from "./WordReveal";

/*
  Full-bleed hero. `video` is the single swappable background source
  (autoplay/muted/loop/playsinline) and `image` is the poster + fallback.
  When no `video` is passed, the poster still gets a slow cinematic
  Ken-Burns drift. Both sit under a dark warm gradient for legibility.
*/
const HeroMedia = ({ image, video }) => {
  const ref = useRef(null);

  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    // Set muted on the DOM element — React's `muted` prop isn't reliably
    // applied, which blocks autoplay on iOS/Android.
    v.muted = true;
    v.defaultMuted = true;
    v.setAttribute("muted", "");
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      v.removeAttribute("autoplay");
      v.pause();
      return;
    }
    const tryPlay = () => v.play().catch(() => {});
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) tryPlay();
        else v.pause();
      },
      { threshold: 0.1 },
    );
    io.observe(v);
    v.addEventListener("loadeddata", tryPlay);
    return () => {
      io.disconnect();
      v.removeEventListener("loadeddata", tryPlay);
    };
  }, []);

  if (!video)
    return (
      <img
        src={image}
        alt=""
        className="ken-burns h-full w-full object-cover"
        fetchPriority="high"
      />
    );

  return (
    <video
      ref={ref}
      className="h-full w-full object-cover"
      poster={image}
      src={video}
      muted
      loop
      autoPlay
      playsInline
      preload="metadata"
      aria-hidden="true"
    />
  );
};

export const Hero = ({
  image,
  video,
  eyebrow,
  title,
  subline,
  primary,
  secondary,
  align = "left",
}) => {
  const alignCls =
    align === "center" ? "items-center text-center" : "items-start text-left";
  return (
    <section
      className="relative flex min-h-[100svh] w-full overflow-hidden"
      data-testid="hero"
    >
      <div className="absolute inset-0">
        <HeroMedia image={image} video={video} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#141210] via-[#141210]/55 to-[#141210]/35" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#141210]/70 via-transparent to-transparent" />
      </div>

      <div className="relative z-10 mx-auto flex w-full max-w-7xl flex-col justify-end px-6 pb-16 pt-36 md:px-12 md:pb-24">
        <div className={`flex max-w-3xl flex-col gap-5 md:gap-7 ${alignCls}`}>
          <span
            className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--mint)] opacity-0"
            style={{ animation: "wordIn .7s var(--ease) .15s forwards" }}
          >
            {eyebrow}
          </span>

          <h1 className="t-hero text-white">
            <WordReveal text={title} start={300} step={85} />
          </h1>

          <p
            className="max-w-xl text-base leading-relaxed text-white/80 sm:text-lg opacity-0"
            style={{ animation: "wordIn .8s var(--ease) .9s forwards" }}
          >
            {subline}
          </p>

          <div
            className={`mt-2 flex flex-col gap-3 sm:flex-row ${align === "center" ? "justify-center" : ""} opacity-0`}
            style={{ animation: "wordIn .8s var(--ease) 1.1s forwards" }}
          >
            {primary && (
              <Button {...(primary.to ? { to: primary.to } : { href: primary.href })} variant="primary" data-testid="hero-primary-cta">
                {primary.label}
              </Button>
            )}
            {secondary && (
              <Button {...(secondary.to ? { to: secondary.to } : { href: secondary.href })} variant="ghostDark" data-testid="hero-secondary-cta">
                {secondary.label}
              </Button>
            )}
          </div>
          {primary?.label === "Install your department" && (
            <p
              className={`text-sm text-white/55 opacity-0 ${align === "center" ? "text-center" : ""}`}
              style={{ animation: "wordIn .8s var(--ease) 1.25s forwards" }}
            >
              Building a team takes months. Installing your department starts with a quick audit and one conversation.
            </p>
          )}
        </div>
      </div>
    </section>
  );
};
